import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  Avatar,
  TextField,
  Switch,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getDocumentVersionDetail,
  updateDocumentVersion,
  uploadDocumentVersion,
  deleteDocumentVersion,
  downloadDocumentVersion,
  downloadDocumentVersionBlob,
  getFileExtensionFromContentType,
} from '../../endpoints/documents';
import { processLinkUrl } from '../../utils/urlUtils';

const DocumentVersionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id, versionId } = useParams<{ id: string; versionId: string }>();

  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    description: '',
    video_url: '',
    link_url: '',
    is_active: false,
    is_public: false,
    is_draft: false,
  });
  const [linkUrlError, setLinkUrlError] = useState<string>('');
  const [videoUrlError, setVideoUrlError] = useState<string>('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [removingDocument, setRemovingDocument] = useState(false);

  // Fetch document data
  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = useApiGet(
    ['document-version-detail', id, versionId],
    () => getDocumentVersionDetail(id!, versionId || 'current'),
    { enabled: !!id }
  );

  // Populate edit form data when document loads
  useEffect(() => {
    if (document) {
      setEditFormData({
        description: document.description || '',
        video_url: document.video_url || '',
        link_url: document.link_url || '',
        is_active: document.is_active || false,
        is_public: document.is_public || false,
        is_draft: document.is_draft || false,
      });
    }
  }, [document]);

  const handleBackToDocuments = () => {
    navigate('/library/documents');
  };

  const handleViewDocument = async () => {
    try {
      const response = await downloadDocumentVersion(
        id!,
        versionId || 'current'
      );

      // Open the presigned URL in a new tab for viewing
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document. Please try again.');
    }
  };

  const handleDownloadDocument = async () => {
    try {
      const response = await downloadDocumentVersionBlob(
        id!,
        versionId || 'current'
      );

      // Determine the filename with proper extension
      let filename = response.filename;
      if (!filename) {
        // Fallback to document name if no filename from API
        const baseName =
          document?.document_name ||
          `document-${id}-version-${versionId || 'current'}`;
        const extension = response.contentType
          ? getFileExtensionFromContentType(response.contentType)
          : '';
        filename = baseName + extension;
      }

      // Create a download link
      const url = window.URL.createObjectURL(response.blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleEditVersion = () => {
    setEditMode(!editMode);
  };

  const handleUploadDocument = async (file: File) => {
    setUploadingDocument(true);
    try {
      const result = await uploadDocumentVersion(
        id!,
        versionId || 'current',
        file
      );
      if (result.success) {
        // Show success message or default message
        if (result.message) {
          alert(result.message);
        } else {
          alert('Document uploaded successfully!');
        }
        // Refresh the document data
        refetch();
      } else {
        // Show error message or default error
        const errorMessage = result.message || 'Upload failed';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to upload document: ${errorMessage}`);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (
      !window.confirm(
        'Are you sure you want to remove this document file? This action cannot be undone.'
      )
    ) {
      return;
    }

    setRemovingDocument(true);
    try {
      const result = await deleteDocumentVersion(id!, versionId || 'current');
      if (result.success) {
        // Show success message or default message
        if (result.message) {
          alert(result.message);
        } else {
          alert('Document removed successfully!');
        }
        // Refresh the document data
        refetch();
      } else {
        // Show error message or default error
        const errorMessage = result.message || 'Remove failed';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error removing document:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to remove document: ${errorMessage}`);
    } finally {
      setRemovingDocument(false);
    }
  };

  const handleCopyLink = () => {
    if (id && versionId) {
      const link = `${window.location.origin}/library/documents/${id}/view/${versionId}`;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          console.log('Link copied:', link);
          alert('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link:', err);
          // Fallback: show the link in an alert
          alert(`Link: ${link}`);
        });
    }
  };

  const { mutate: updateVersionMutation, isPending: isUpdating } = useApiSend(
    (data: {
      description: string;
      video_url: string;
      link_url: string;
      is_active: boolean;
      is_public: boolean;
      is_draft: boolean;
    }) =>
      updateDocumentVersion(id!, versionId!, {
        version: document?.version || '',
        description: data.description,
        video_url: data.video_url,
        link_url: data.link_url,
        is_active: data.is_active,
        is_public: data.is_public,
        is_draft: data.is_draft,
      }),
    (data) => {
      console.log('Version updated successfully:', data);
      setEditMode(false);
      // Refresh the document data
      refetch();
    },
    (error) => {
      console.error('Error updating version:', error);
      // Keep edit mode on so user can fix and retry
    },
    [['document-version-detail', id, versionId]]
  );

  const handleSave = () => {
    // Validate video_url if it's provided
    if (editFormData.video_url.trim()) {
      const videoUrl = editFormData.video_url.trim();

      // Check if it's an internal link (starts with /) or external link (starts with http:// or https://)
      if (
        !videoUrl.startsWith('/') &&
        !videoUrl.startsWith('http://') &&
        !videoUrl.startsWith('https://')
      ) {
        setVideoUrlError(
          'Video URL must start with / for internal links or http:///https:// for external links'
        );
        return;
      }

      // Clear any previous errors
      setVideoUrlError('');
    }

    // Validate link_url if it's provided
    if (editFormData.link_url.trim()) {
      const linkUrl = editFormData.link_url.trim();

      // Check if it's an internal link (starts with /) or external link (starts with http:// or https://)
      if (
        !linkUrl.startsWith('/') &&
        !linkUrl.startsWith('http://') &&
        !linkUrl.startsWith('https://')
      ) {
        setLinkUrlError(
          'Link URL must start with / for internal links or http:///https:// for external links'
        );
        return;
      }

      // Clear any previous errors
      setLinkUrlError('');
    }

    updateVersionMutation(editFormData);
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form data to original values
    if (document) {
      setEditFormData({
        description: document.description || '',
        video_url: document.video_url || '',
        link_url: document.link_url || '',
        is_active: document.is_active || false,
        is_public: document.is_public || false,
        is_draft: document.is_draft || false,
      });
    }
  };

  const handleDeleteVersion = () => {
    // TODO: Implement delete version functionality
    console.log('Deleting version:', versionId);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading document version...</Typography>
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading document version</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToDocuments}
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Link
          component={RouterLink}
          to={`/library/documents/${id}/edit`}
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          {document?.document_name}
        </Link>
        <Typography variant="body2" color="text.primary">
          Version {document?.version}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {document.document_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`Version ${document?.version}`}
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Created on{' '}
              {document?.created_at
                ? new Date(document.created_at).toLocaleDateString()
                : new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Document Version Information */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Version Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Version Number
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {document?.version}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editFormData.description}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      multiline
                      rows={3}
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.description || 'No description available'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="body1">
                      {document?.created_by || 'admin@peo.org'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <CalendarIcon fontSize="small" />
                    <Typography variant="body1">
                      {document?.created_at
                        ? new Date(document.created_at).toLocaleDateString()
                        : new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Downloadable
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={document?.downloadable ? 'Yes' : 'No'}
                      size="small"
                      color={document?.downloadable ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_active}
                        onChange={(e) => {
                          const newActiveState = e.target.checked;
                          setEditFormData((prev) => ({
                            ...prev,
                            is_active: newActiveState,
                            // If setting to active, ensure draft is false
                            is_draft: newActiveState ? false : prev.is_draft,
                          }));
                        }}
                        disabled={editFormData.is_draft}
                        color="success"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                      {editFormData.is_draft && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Cannot be active while in draft mode
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={document?.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Public
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_public}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            is_public: e.target.checked,
                          }))
                        }
                        color="primary"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_public ? 'Public' : 'Private'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_public ? 'Public' : 'Private'}
                        size="small"
                        color={document?.is_public ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Draft
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_draft}
                        onChange={(e) => {
                          const newDraftState = e.target.checked;
                          setEditFormData((prev) => ({
                            ...prev,
                            is_draft: newDraftState,
                            // If setting to draft, ensure active is false
                            is_active: newDraftState ? false : prev.is_active,
                          }));
                        }}
                        disabled={editFormData.is_active}
                        color="warning"
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_draft ? 'Draft' : 'Final'}
                      </Typography>
                      {editFormData.is_active && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Cannot be draft while active
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_draft ? 'Draft' : 'Final'}
                        size="small"
                        color={document?.is_draft ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Video URL
                  </Typography>
                  {editMode ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={editFormData.video_url}
                        onChange={(e) => {
                          setEditFormData((prev) => ({
                            ...prev,
                            video_url: e.target.value,
                          }));
                          // Clear error when user starts typing
                          if (videoUrlError) {
                            setVideoUrlError('');
                          }
                        }}
                        placeholder="Enter video URL"
                        error={!!videoUrlError}
                        sx={{ mb: 2 }}
                      />
                      {videoUrlError && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {videoUrlError}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.video_url ? (
                        <Link
                          href={document.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {document.video_url}
                        </Link>
                      ) : (
                        'No video URL set'
                      )}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Link URL
                  </Typography>
                  {editMode ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={editFormData.link_url}
                        onChange={(e) => {
                          setEditFormData((prev) => ({
                            ...prev,
                            link_url: e.target.value,
                          }));
                          // Clear error when user starts typing
                          if (linkUrlError) {
                            setLinkUrlError('');
                          }
                        }}
                        placeholder="Enter link URL"
                        error={!!linkUrlError}
                        sx={{ mb: 2 }}
                      />
                      {linkUrlError && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {linkUrlError}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.link_url ? (
                        <Link
                          href={processLinkUrl(document.link_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {document.link_url}
                        </Link>
                      ) : (
                        'No link URL set'
                      )}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                {document?.downloadable && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={handleViewDocument}
                      sx={{
                        color: '#FF6B35',
                        borderColor: '#FF6B35',
                        '&:hover': {
                          borderColor: '#E55A2B',
                          backgroundColor: 'rgba(255, 107, 53, 0.04)',
                        },
                      }}
                    >
                      View Document
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadDocument}
                      sx={{
                        backgroundColor: '#00A859',
                        '&:hover': {
                          backgroundColor: '#008A4A',
                        },
                      }}
                    >
                      Download Document
                    </Button>
                  </>
                )}

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  sx={{
                    color: '#6C63FF',
                    borderColor: '#6C63FF',
                    '&:hover': {
                      borderColor: '#5A52E0',
                      backgroundColor: 'rgba(108, 99, 255, 0.04)',
                    },
                  }}
                >
                  Copy Link
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Version Management */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Version Management
              </Typography>
              <Stack spacing={2}>
                {editMode ? (
                  <>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        backgroundColor: '#0072CE',
                        '&:hover': { backgroundColor: '#005a9e' },
                      }}
                      startIcon={<EditIcon />}
                      onClick={handleSave}
                      disabled={isUpdating}
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      color: '#0072CE',
                      borderColor: '#0072CE',
                      '&:hover': {
                        borderColor: '#005a9e',
                        backgroundColor: 'rgba(0, 114, 206, 0.04)',
                      },
                    }}
                    startIcon={<EditIcon />}
                    onClick={handleEditVersion}
                    disabled={isUpdating}
                  >
                    Edit Version
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteVersion}
                >
                  Delete Version
                </Button>

                {/* Document File Management */}
                {!document?.downloadable ? (
                  <Button
                    component="label"
                    fullWidth
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    disabled={uploadingDocument || removingDocument}
                    sx={{
                      color: '#00A859',
                      borderColor: '#00A859',
                      '&:hover': {
                        borderColor: '#008A4A',
                        backgroundColor: 'rgba(0, 168, 89, 0.04)',
                      },
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleUploadDocument(file);
                        }
                      }}
                    />
                    {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveDocument}
                    disabled={uploadingDocument || removingDocument}
                  >
                    {removingDocument ? 'Removing...' : 'Remove Document'}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentVersionDetail;
